// src/pages/caisse/Decaissement.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Spinner, 
    InputGroup, Badge, Modal, Table, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import SignatureCanvas from 'react-signature-canvas';
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { API_BASE_URL } from "../../constants/api";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Decaissement() {
    const navigate = useNavigate();
    const location = useLocation();
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [showCachetModal, setShowCachetModal] = useState(false);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [signaturePad, setSignaturePad] = useState(null);
    const [cachetPad, setCachetPad] = useState(null);
    const [numeroBordereau, setNumeroBordereau] = useState("");
    const [existingOperations, setExistingOperations] = useState([]);
    const [selectedParent, setSelectedParent] = useState(null);
    const [loadingExisting, setLoadingExisting] = useState(false);

    const [operation, setOperation] = useState({
        beneficiaire_nom: '',
        beneficiaire_prenom: '',
        montant: '',
        motif: '',
        avance: '',
        reste: '',
        fait_a: '',
        date_signature: new Date().toISOString().split('T')[0],
        signature: null,
        cachet: null,
        created_by: null,
        parent_id: null,
        type: 'initial'
    });

    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") return JSON.parse(userStr);
        } catch (error) {
            console.error("Erreur parsing utilisateur:", error);
            localStorage.removeItem("user");
        }
        return null;
    };

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        const state = location.state;
        if (state && state.parent_id) {
            const resteValue = state.reste || 0;
            setOperation(prev => ({
                ...prev,
                parent_id: state.parent_id,
                type: 'paiement',
                beneficiaire_nom: state.beneficiaire_nom || '',
                beneficiaire_prenom: state.beneficiaire_prenom || '',
                motif: state.motif || '',
                montant: resteValue,
                avance: '',
                reste: resteValue
            }));
            setSelectedParent({
                id: state.parent_id,
                numero_bordereau: state.numero_bordereau || 'Nouveau',
                beneficiaire_prenom: state.beneficiaire_prenom || '',
                beneficiaire_nom: state.beneficiaire_nom || '',
                reste: resteValue,
                montant_initial: state.montant_initial || resteValue,
                avance_actuelle: state.avance_actuelle || 0
            });
            showToast('info', 'Information', `Pré-remplissage effectué. Reste à payer: ${formatCurrency(resteValue)}`);
        }
        const currentUser = getCurrentUser();
        if (currentUser) {
            const userId = currentUser.id_utilisateur || currentUser.id;
            setOperation(prev => ({ ...prev, created_by: userId }));
        }
    }, [location.state]);

    useEffect(() => {
        const montant = parseFloat(operation.montant) || 0;
        const avance = parseFloat(operation.avance) || 0;
        const nouveauReste = Math.max(0, montant - avance);
        setOperation(prev => ({ ...prev, reste: nouveauReste }));
    }, [operation.montant, operation.avance]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchNonSoldesOperations = async () => {
        try {
            setLoadingExisting(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?action=non_soldes`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) setExistingOperations(result.data || []);
        } catch (err) {
            console.error("Erreur chargement décaissements:", err);
        } finally {
            setLoadingExisting(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleSelectExisting = (op) => {
        setSelectedParent(op);
        setOperation({
            ...operation,
            parent_id: op.id,
            type: 'paiement',
            beneficiaire_nom: op.beneficiaire_nom,
            beneficiaire_prenom: op.beneficiaire_prenom,
            motif: op.motif,
            montant: op.reste_bdd,
            avance: '',
            reste: op.reste_bdd,
            fait_a: operation.fait_a,
            date_signature: operation.date_signature,
            signature: operation.signature,
            cachet: operation.cachet
        });
        setShowSelectModal(false);
        showToast('info', 'Information', `Décaissement sélectionné: ${op.numero_bordereau} - Reste: ${formatCurrency(op.reste_bdd)}`);
    };

    const handleNewDecaissement = () => {
        setSelectedParent(null);
        setOperation({
            ...operation,
            parent_id: null,
            type: 'initial',
            beneficiaire_nom: '',
            beneficiaire_prenom: '',
            montant: '',
            motif: '',
            avance: '',
            reste: '',
            fait_a: '',
            date_signature: new Date().toISOString().split('T')[0],
            signature: null,
            cachet: null
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setOperation(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!operation.beneficiaire_nom || !operation.beneficiaire_prenom || !operation.motif) {
            showToast('error', 'Champ requis', 'Veuillez remplir tous les champs obligatoires');
            return;
        }
        if (!operation.parent_id && (!operation.montant || parseFloat(operation.montant) <= 0)) {
            showToast('error', 'Champ requis', 'Le montant total doit être supérieur à 0');
            return;
        }
        if (operation.parent_id && (!operation.avance || parseFloat(operation.avance) <= 0)) {
            showToast('error', 'Champ requis', 'Veuillez saisir le montant à payer');
            return;
        }
        if (operation.parent_id && parseFloat(operation.avance) > parseFloat(operation.montant)) {
            showToast('error', 'Erreur', `Le montant à payer ne peut pas dépasser ${formatCurrency(operation.montant)}`);
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            let dataToSend;

            if (operation.parent_id) {
                const montantInitial = parseFloat(operation.montant);
                const montantPaye = parseFloat(operation.avance);
                const nouveauReste = montantInitial - montantPaye;
                dataToSend = {
                    beneficiaire_nom: operation.beneficiaire_nom,
                    beneficiaire_prenom: operation.beneficiaire_prenom,
                    montant: montantInitial,
                    motif: operation.motif,
                    avance: montantPaye,
                    reste: nouveauReste,
                    fait_a: operation.fait_a || null,
                    date_signature: operation.date_signature,
                    signature: operation.signature || null,
                    cachet: operation.cachet || null,
                    created_by: operation.created_by,
                    parent_id: operation.parent_id,
                    type: 'paiement'
                };
            } else {
                const montantTotal = parseFloat(operation.montant);
                const avance = parseFloat(operation.avance) || 0;
                const reste = montantTotal - avance;
                dataToSend = {
                    beneficiaire_nom: operation.beneficiaire_nom,
                    beneficiaire_prenom: operation.beneficiaire_prenom,
                    montant: montantTotal,
                    motif: operation.motif,
                    avance: avance,
                    reste: reste,
                    fait_a: operation.fait_a || null,
                    date_signature: operation.date_signature,
                    signature: operation.signature || null,
                    cachet: operation.cachet || null,
                    created_by: operation.created_by,
                    parent_id: null,
                    type: 'initial',
                    montant_initial: montantTotal
                };
            }

            const response = await fetch(`${API_BASE_URL}/caisse.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSend)
            });
            const result = await response.json();
            if (result.success) {
                setNumeroBordereau(result.numero_bordereau);
                showToast('success', 'Décaissement enregistré', `Bordereau: ${result.numero_bordereau}`);
                setTimeout(() => {
                    resetForm();
                    navigate(-1);
                }, 2500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            console.log(err);
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        const currentUser = getCurrentUser();
        const userId = currentUser?.id_utilisateur || currentUser?.id || null;
        setOperation({
            beneficiaire_nom: '',
            beneficiaire_prenom: '',
            montant: '',
            motif: '',
            avance: '',
            reste: '',
            fait_a: '',
            date_signature: new Date().toISOString().split('T')[0],
            signature: null,
            cachet: null,
            created_by: userId,
            parent_id: null,
            type: 'initial'
        });
        setSelectedParent(null);
        setNumeroBordereau("");
    };

    const saveSignature = () => {
        if (signaturePad && !signaturePad.isEmpty()) {
            setOperation(prev => ({ ...prev, signature: signaturePad.toDataURL() }));
            setShowSignatureModal(false);
            showToast('info', 'Signature', 'Signature ajoutée avec succès');
        } else {
            showToast('warning', 'Attention', 'Veuillez signer dans la zone prévue');
        }
    };

    const saveCachet = () => {
        if (cachetPad && !cachetPad.isEmpty()) {
            setOperation(prev => ({ ...prev, cachet: cachetPad.toDataURL() }));
            setShowCachetModal(false);
            showToast('info', 'Cachet', 'Cachet ajouté avec succès');
        } else {
            showToast('warning', 'Attention', 'Veuillez apposer le cachet');
        }
    };

    const clearSignature = () => { if (signaturePad) signaturePad.clear(); };
    const clearCachet = () => { if (cachetPad) cachetPad.clear(); };
    const formatMontant = (montant) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(montant);

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    <ToastNotification
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">
                                    {operation.parent_id ? 'Continuer le paiement' : 'Nouveau décaissement'}
                                </h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-line"></i>
                                        {operation.parent_id
                                            ? `Suite: ${selectedParent?.numero_bordereau}`
                                            : 'Enregistrer un nouveau décaissement'}
                                    </span>
                                    {numeroBordereau && (
                                        <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-barcode-line"></i> {numeroBordereau}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => { fetchNonSoldesOperations(); setShowSelectModal(true); }}>
                                <i className="ri-folder-line"></i> Choisir existant
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/caisse/operations')}>
                                <i className="ri-history-line"></i> Historique
                            </Button>
                        </div>
                    </div>

                    {/* ===== FORMULAIRE ===== */}
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient-secondary">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-bank-card-line me-2"></i>
                                        Formulaire de décaissement
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">

                                    {/* Carte décaissement en cours */}
                                    {selectedParent && (
                                        <div className="parent-card-charte mb-4">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="parent-icon-charte">
                                                        <i className="ri-folder-received-line"></i>
                                                    </div>
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                            <span className="fw-semibold" style={{ color: COLORS.success, fontSize: '0.95rem' }}>
                                                                Décaissement en cours
                                                            </span>
                                                            <span className="badge-parent-partiel-charte">
                                                                <i className="ri-time-line"></i>Paiement partiel
                                                            </span>
                                                        </div>
                                                        <div className="d-flex flex-wrap gap-3">
                                                            <div>
                                                                <span className="parent-label-charte">Bordereau</span>
                                                                <div className="parent-value-charte code">{selectedParent.numero_bordereau}</div>
                                                            </div>
                                                            <div>
                                                                <span className="parent-label-charte">Bénéficiaire</span>
                                                                <div className="parent-value-charte">
                                                                    {selectedParent.beneficiaire_prenom} {selectedParent.beneficiaire_nom}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <span className="parent-label-charte">Montant total</span>
                                                                <div className="parent-value-charte">{formatCurrency(selectedParent.montant_initial)}</div>
                                                            </div>
                                                            <div>
                                                                <span className="parent-label-charte">Reste à payer</span>
                                                                <div className="parent-value-charte reste">{formatCurrency(selectedParent.reste)}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button onClick={handleNewDecaissement} className="btn-modern-outline" size="sm">
                                                    <i className="ri-add-line me-1"></i>Nouveau
                                                </Button>
                                            </div>

                                            {/* Barre de progression */}
                                            {selectedParent.montant_initial > 0 && (
                                                <div className="mt-3 pt-2">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <small className="text-muted">Progression du paiement</small>
                                                        <small className="fw-semibold" style={{ color: COLORS.success }}>
                                                            {selectedParent.pourcentage_paye
                                                                ? selectedParent.pourcentage_paye.toFixed(1)
                                                                : Math.round(((selectedParent.montant_initial - selectedParent.reste) / selectedParent.montant_initial) * 100)}%
                                                        </small>
                                                    </div>
                                                    <div className="progress-charte">
                                                        <div
                                                            className="progress-bar-charte"
                                                            style={{
                                                                width: `${selectedParent.pourcentage_paye || ((selectedParent.montant_initial - selectedParent.reste) / selectedParent.montant_initial) * 100}%`
                                                            }}
                                                        ></div>
                                                    </div>
                                                    <div className="d-flex justify-content-between mt-1">
                                                        <small className="text-muted">Payé: {formatCurrency(selectedParent.avance_actuelle || 0)}</small>
                                                        <small className="text-muted">Total: {formatCurrency(selectedParent.montant_initial)}</small>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">

                                            {/* SECTION BÉNÉFICIAIRE */}
                                            <Col md={12}>
                                                <div className="section-title-charte">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                        <i className="ri-user-line"></i>
                                                    </span>
                                                    <h6>Informations du bénéficiaire</h6>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Nom <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="beneficiaire_nom"
                                                            value={operation.beneficiaire_nom}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Nom du bénéficiaire"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Prénom <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-smile-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="beneficiaire_prenom"
                                                            value={operation.beneficiaire_prenom}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Prénom du bénéficiaire"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION MONTANTS */}
                                            <Col md={12}>
                                                <div className="section-title-charte mt-2">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                        <i className="ri-money-euro-circle-line"></i>
                                                    </span>
                                                    <h6>Montants</h6>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        {operation.parent_id ? 'Reste avant paiement' : 'Montant total'} <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            name="montant"
                                                            value={operation.montant}
                                                            onChange={handleChange}
                                                            required={!operation.parent_id}
                                                            placeholder="0"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                        <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        {operation.parent_id ? 'Montant à payer' : 'Avance (optionnel)'}
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-hand-coin-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max={operation.parent_id ? operation.montant : undefined}
                                                            name="avance"
                                                            value={operation.avance}
                                                            onChange={handleChange}
                                                            placeholder="0"
                                                        />
                                                        <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                    </InputGroup>
                                                    {operation.parent_id && (
                                                        <Form.Text className="text-muted small">
                                                            Maximum: {formatCurrency(operation.montant)}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Nouveau reste</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-hourglass-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={formatMontant(operation.reste)}
                                                            disabled
                                                            className="bg-light"
                                                            style={{ fontWeight: 'bold', color: operation.reste > 0 ? COLORS.danger : COLORS.success }}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION DÉTAILS */}
                                            <Col md={12}>
                                                <div className="section-title-charte mt-2">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                        <i className="ri-information-line"></i>
                                                    </span>
                                                    <h6>Détails</h6>
                                                </div>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Motif <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-question-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="motif"
                                                            value={operation.motif}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Décrivez le motif du décaissement"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Fait à</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="fait_a"
                                                            value={operation.fait_a}
                                                            onChange={handleChange}
                                                            placeholder="Lieu"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            name="date_signature"
                                                            value={operation.date_signature}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION SIGNATURE & CACHET */}
                                            <Col md={12}>
                                                <div className="section-title-charte mt-2">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                        <i className="ri-pen-line"></i>
                                                    </span>
                                                    <h6>Signature & Cachet</h6>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Signature</Form.Label>
                                                    <div className="d-flex gap-2 flex-wrap">
                                                        <Button
                                                            onClick={() => setShowSignatureModal(true)}
                                                            className="btn-modern-outline"
                                                            size="sm"
                                                        >
                                                            <i className="ri-pen-line me-2"></i>
                                                            {operation.signature ? "Modifier la signature" : "Ajouter une signature"}
                                                        </Button>
                                                        {operation.signature && (
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => setOperation(prev => ({ ...prev, signature: null }))}
                                                                className="btn-modern-danger-outline"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {operation.signature && (
                                                        <div className="signature-preview-charte mt-2">
                                                            <img src={operation.signature} alt="Signature" />
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Cachet</Form.Label>
                                                    <div className="d-flex gap-2 flex-wrap">
                                                        <Button
                                                            onClick={() => setShowCachetModal(true)}
                                                            className="btn-modern-outline"
                                                            size="sm"
                                                        >
                                                            <i className="ri-stamp-line me-2"></i>
                                                            {operation.cachet ? "Modifier le cachet" : "Ajouter un cachet"}
                                                        </Button>
                                                        {operation.cachet && (
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => setOperation(prev => ({ ...prev, cachet: null }))}
                                                                className="btn-modern-danger-outline"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {operation.cachet && (
                                                        <div className="signature-preview-charte mt-2">
                                                            <img src={operation.cachet} alt="Cachet" />
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>

                                            {/* BOUTONS D'ACTION */}
                                            <Col md={12}>
                                                <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                                <div className="d-flex justify-content-end gap-3 flex-wrap">
                                                    <Button onClick={resetForm} className="btn-modern-outline">
                                                        Réinitialiser
                                                    </Button>
                                                    <Button type="submit" disabled={saving} className="btn-modern-primary">
                                                        {saving ? (
                                                            <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                                        ) : (
                                                            <><i className="ri-save-line me-2"></i>Enregistrer</>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SÉLECTION DÉCAISSEMENT EXISTANT ===== */}
            <Modal show={showSelectModal} onHide={() => setShowSelectModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}, #0d8ba6)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-folder-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Choisir un décaissement existant</Modal.Title>
                            <p className="text-white-50 mb-0 small">Sélectionnez un décaissement non soldé à compléter</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {loadingExisting ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" style={{ color: COLORS.primary }} />
                        </div>
                    ) : existingOperations.length === 0 ? (
                        <div className="empty-state-charte">
                            <div className="empty-state-icon-charte"><i className="ri-inbox-line"></i></div>
                            <h6 className="fw-semibold mb-1">Aucun décaissement non soldé</h6>
                            <p className="text-muted small mb-0">Tous les décaissements sont soldés</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0 table-charte">
                                <thead>
                                    <tr>
                                        <th className="ps-4">Bordereau</th>
                                        <th>Bénéficiaire</th>
                                        <th>Motif</th>
                                        <th className="text-end">Montant initial</th>
                                        <th className="text-end">Reste</th>
                                        <th>Date</th>
                                        <th className="text-center pe-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingOperations.map(op => (
                                        <tr key={op.id}>
                                            <td className="ps-4">
                                                <span className="reference-badge-charte">
                                                    <i className="ri-barcode-line"></i>{op.numero_bordereau}
                                                </span>
                                            </td>
                                            <td className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                {op.beneficiaire_prenom} {op.beneficiaire_nom}
                                            </td>
                                            <td className="text-muted small">
                                                {op.motif?.substring(0, 30)}...
                                            </td>
                                            <td className="text-end fw-semibold">{formatCurrency(op.montant_initial)}</td>
                                            <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                {formatCurrency(op.reste_reel)}
                                            </td>
                                            <td className="text-muted small">{formatDate(op.created_at)}</td>
                                            <td className="text-center pe-4">
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleSelectExisting(op)}
                                                    className="btn-modern-success"
                                                >
                                                    <i className="ri-add-line me-1"></i>Continuer
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button variant="outline-secondary" onClick={() => setShowSelectModal(false)} className="btn-modern-outline">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL SIGNATURE ===== */}
            <Modal show={showSignatureModal} onHide={() => setShowSignatureModal(false)} centered size="md" className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-pen-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Signature</Modal.Title>
                            <p className="text-white-50 mb-0 small">Signez dans la zone ci-dessous</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="signature-container-charte">
                        <SignatureCanvas
                            ref={(ref) => setSignaturePad(ref)}
                            canvasProps={{
                                width: 500,
                                height: 200,
                                className: 'signature-canvas',
                                style: { width: '100%', height: '200px' }
                            }}
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                        <Button onClick={clearSignature} className="btn-modern-outline">
                            <i className="ri-refresh-line me-2"></i>Effacer
                        </Button>
                        <Button onClick={saveSignature} className="btn-modern-primary">
                            <i className="ri-check-line me-2"></i>Valider
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* ===== MODAL CACHET ===== */}
            <Modal show={showCachetModal} onHide={() => setShowCachetModal(false)} centered size="md" className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-stamp-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Cachet</Modal.Title>
                            <p className="text-white-50 mb-0 small">Apposez le cachet dans la zone ci-dessous</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="signature-container-charte">
                        <SignatureCanvas
                            ref={(ref) => setCachetPad(ref)}
                            canvasProps={{
                                width: 500,
                                height: 200,
                                className: 'cachet-canvas',
                                style: { width: '100%', height: '200px' }
                            }}
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                        <Button onClick={clearCachet} className="btn-modern-outline">
                            <i className="ri-refresh-line me-2"></i>Effacer
                        </Button>
                        <Button onClick={saveCachet} className="btn-modern-warning">
                            <i className="ri-check-line me-2"></i>Valider
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient-secondary {
                    background: linear-gradient(135deg, var(--secondary), #0d8ba6);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== PARENT CARD ===== */
                .parent-card-charte {
                    background: linear-gradient(135deg, #ecfdf5, #d1fae5);
                    border: 1px solid #a7f3d0;
                    border-radius: 14px;
                    padding: 1.25rem;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
                }
                .parent-icon-charte {
                    width: 52px; height: 52px;
                    background: rgba(16, 185, 129, 0.15);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem;
                    color: var(--success);
                    flex-shrink: 0;
                }
                .badge-parent-partiel-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    color: white;
                    padding: 0.2rem 0.65rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 600;
                }
                .parent-label-charte {
                    display: block;
                    color: #64748b;
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    font-weight: 600;
                    margin-bottom: 2px;
                }
                .parent-value-charte {
                    font-weight: 600;
                    color: #065f46;
                    font-size: 0.85rem;
                }
                .parent-value-charte.code { font-family: monospace; }
                .parent-value-charte.reste {
                    color: var(--danger);
                    font-size: 1rem;
                    font-weight: 700;
                }

                .progress-charte {
                    height: 8px; border-radius: 6px;
                    background: #d1fae5; overflow: hidden;
                }
                .progress-bar-charte {
                    height: 100%; border-radius: 6px;
                    background: linear-gradient(90deg, var(--success), #059669);
                    transition: width 0.6s ease;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== SIGNATURE PREVIEW ===== */
                .signature-preview-charte img {
                    max-width: 200px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== SIGNATURE CANVAS ===== */
                .signature-container-charte {
                    border: 2px dashed #cbd5e1;
                    border-radius: 12px;
                    padding: 10px;
                    background: white;
                }
                .signature-container-charte canvas {
                    background: white;
                    cursor: crosshair;
                    border-radius: 8px;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-success, .btn-modern-warning {
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-danger-outline {
                    border: 1.5px solid var(--danger);
                    background: white; color: var(--danger);
                    padding: 0.5rem 1rem; border-radius: 50px;
                    font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger-outline:hover {
                    background: var(--danger); color: white;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                }
                .btn-modern-primary:disabled,
                .btn-modern-success:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .parent-icon-charte { width: 44px; height: 44px; font-size: 1.2rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}